import { useIsMutating } from "@tanstack/react-query";
import {
  AnimationComponent,
  ArrowLeftIcon,
  ArrowRightIcon,
  BulletedListIcon,
  CardsThreeIcon,
  CheckIcon,
  CheckListIcon,
  CircularProgress,
  CreditCardIcon,
  Divider,
  DocumentIcon,
  DownloadIcon,
  GKButton,
  Grid2,
  HandCoinIcon,
  Show,
  Tab,
  Tabs,
  UserCheckIcon,
  UserRectangleIcon,
} from "components";
import dayjs from "dayjs";
import useEnvironmentCheck from "hooks/useEnvironmentCheck";
import { useFetchQuery, useMutation } from "hooks/useQueries";
import { isEmpty, sum } from "lodash";
import { FormProvider, useForm } from "react-hook-form";
import { useLocation, useSearchParams } from "react-router-dom";
import axiosInstance from "utils/axiosInstance";
import { refetchQuery } from "utils/helpers";
import Agreementtab from "./Agreementtab";
import AssessmentTab from "./AssessmentTab";
import BasketTab from "./BasketTab";
import CompletedStep from "./CompletedStep";
import DetailsTab from "./DetailsTab";
import InvestmentTab from "./InvestmentTab";
import { useInvestorContext } from "./InvestorContext";
import KYCTab from "./KYCTab";
import PaymentTab from "./PaymentTab";
import PlansHoldingsModal from "./PlansHoldingsModal";
import UploadPlanProofs from "./UploadPlanProofs";
import { daysCalculate } from "./utils";

const OnBoarding = () => {
  const location = useLocation();

  const { dispatch } = useInvestorContext();
  const { getLocalEnvironment } = useEnvironmentCheck();

  const [searchParams] = useSearchParams();

  const logo = location.search.split("logo=")[1];
  const clientName = searchParams.get("client_name");
  const clientEmail = searchParams.get("client_email");
  const phone = searchParams.get("phone");
  const basketId = searchParams.get("basket_id");
  const companyId = searchParams.get("company_id");
  const feePlanId = searchParams.get("fee_plan_id");
  const partnerId = searchParams.get("partner_id");
  const clientId = searchParams.get("client_id");
  const token = searchParams.get("token");

  const form = useForm({
    defaultValues: {
      activeStep: 0,
      activeStepText: "DETAILS",
      fetchedFromKra: false,
      clientObject: null,
      retakeAssessment: true,
      selectedFeePlan: {},
      planDataObject: {
        aumCash: "",
        showHoldingsModal: false,
        aumBasedPlan: "",
        subscriptionPlan: null,
        subscriptionPlanAccountType: null,
        holdingsProofs: [],
        cashProofs: [],
        plansTimeperiod: "",
        plansHoldings: [
          {
            gridCode: "",
            quantity: 0,
            cmp: "",
            id: 0,
          },
        ],
      },
      clientCreation: {
        personalDetails: {
          pan: "",
          dateOfBirth: dayjs().format("YYYY-MM-DD"),
          name: clientName || "",
          email: clientEmail || "",
          gender: "",
          maritalStatus: "",
          fatherName: "",
          motherName: "",
          income: 0,
          address: "",
          country: "",
          state: "",
          city: "",
          pincode: "",
        },
        bankAccounts: [],
        dematAccounts: [],
        nominees: [],
        phone: phone || "",
        countryCode: "+91",
        basketId: +basketId || null,
        company: +companyId || null,
        feePlan: feePlanId || null,
        partner: +partnerId || null,
      },
    },
  });

  const { isFetching: existClientLoading } = useFetchQuery({
    key: ["GET_CLIENT_BY_ID_ONBOARDING"],
    route: `/client/${clientId}/`,
    headers: {
      Authorization: `Token ${token}`,
      "x-gridkey-user-role": "client",
    },
    enabled: !!clientId,
    success: (response: any) => {
      form.setValue("clientCreation.personalDetails", {
        ...response?.data?.personalDetails,
        country: "",
        state: "",
        city: "",
      });
      form.setValue(
        "clientCreation.bankAccounts",
        response?.data?.bankAccounts,
      );
      form.setValue(
        "clientCreation.dematAccounts",
        response?.data?.dematAccounts,
      );
      form.setValue("clientCreation.nominees", response?.data?.nominees);
    },
  });

  const activeStepWatch = form.watch("activeStep");
  const holdingModalWatch = form.watch("planDataObject.showHoldingsModal");

  const handleNext = () => form.setValue("activeStep", activeStepWatch + 1);
  const handlePrevious = () => form.setValue("activeStep", activeStepWatch - 1);

  const handleReturnStepColor = ({
    index,
    dataItem,
  }: {
    index: number;
    dataItem: any;
  }) => {
    if (index === activeStepWatch) {
      return "var(--primary-900)";
    }
    if (dataItem?.isCompleted) {
      return "var(--success-700)";
    }
    return "var(--neutral-500)";
  };

  const { isFetching } = useFetchQuery({
    key: ["GET_CLIENT_FOR_CREATION"],
    route: `/client-onboard/?pan=${form.getValues("clientCreation.personalDetails.pan")}&phone=${form.getValues("clientCreation.phone")}&basket_id=${basketId}&company=${companyId}`,
    enabled: activeStepWatch >= 1,
    success: (response: any) => {
      form.setValue("clientObject", response?.data?.data);
      form.setValue(
        "selectedFeePlan",
        response?.data?.data?.basket?.feePlansList?.find(
          (dataItem: { id: string }) => dataItem?.id === feePlanId,
        ),
      );
      form.setValue(
        "retakeAssessment",
        !response?.data?.data?.riskprofileDetails?.points,
      );

      axiosInstance.interceptors.request.use((config) => {
        config.headers.Authorization = `Token ${response?.data?.data?.token}`;
        config.headers[`x-gridkey-user-role`] = `client`;
        return config;
      });

      dispatch({ type: "TOKEN", data: response?.data?.data?.token });
      dispatch({ type: "IS_EXIST_TOKEN", data: true });
    },
  });

  const { mutate: handleClientCreation } = useMutation({
    mutationKey: ["CLIENT_CREATION"],
    mutationFn: (data) => axiosInstance.post("/client-onboard/", data),
    onSuccess: () => {
      refetchQuery("GET_CLIENT_FOR_CREATION");
      handleNext();
    },
  });

  const { mutate: uploadProof } = useMutation({
    mutationKey: ["UPLOAD_PROOF_FOR_ONBOARDING"],
    mutationFn: (values: any) =>
      axiosInstance.patch(
        `/plans/${form.getValues("clientObject.portfolio.id")}/`,
        values,
      ),
    onSuccess: () => {
      handleNext();
      refetchQuery("GET_CLIENT_FOR_CREATION");
    },
  });

  const handleCalculateFee = ({ total, feePercent }: any) => {
    const time =
      daysCalculate(form.getValues("clientObject.advanceFeePlan.frequency")) ||
      0;
    const calc = (time * total * feePercent) / 365 / 100;
    return calc || 0;
  };

  const tabs = [
    {
      name: "Details",
      icon: UserRectangleIcon,
      isCompleted: activeStepWatch !== 0,
      disabled: activeStepWatch !== 0,
      show: true,
      component: <DetailsTab companyId={companyId} basketId={basketId} />,
      value: "DETAILS",
      id: "details",
    },
    {
      name: "Basket",
      icon: CardsThreeIcon,
      isCompleted: form.getValues("clientObject.portfolio.id"),
      disabled: form.getValues("clientObject.portfolio.id"),
      show: form.getValues(
        "clientObject.onboardDetails.customizeSetting.basketFeePlan",
      ),
      component: <BasketTab loading={isFetching} />,
      value: "BASKET",
      id: "basket",
    },
    {
      name: "Investment",
      icon: HandCoinIcon,
      isCompleted:
        !isEmpty(form.getValues("planDataObject.aumBasedPlan")) &&
        activeStepWatch > 2,
      disabled:
        !isEmpty(form.getValues("planDataObject.aumBasedPlan")) &&
        activeStepWatch > 2,
      show: form.getValues(
        "clientObject.onboardDetails.customizeSetting.investmentDetails",
      ),
      component: <InvestmentTab />,
      value: "INVESTMENT",
      id: "investment",
    },
    {
      name: "Upload Proof",
      icon: DownloadIcon,
      isCompleted: activeStepWatch > 3,
      disabled: activeStepWatch > 3,
      show: form.getValues(
        "clientObject.onboardDetails.customizeSetting.proofs",
      ),
      component: <UploadPlanProofs />,
      value: "UPLOAD_PROOF",
      id: "upload_proof",
    },
    {
      name: "KYC",
      icon: UserCheckIcon,
      isCompleted: form.getValues("clientObject.kycDetails.kycId"),
      disabled: form.getValues("clientObject.kycDetails.kycId"),
      show: form.getValues("clientObject.onboardDetails.customizeSetting.kyc"),
      component: (
        <KYCTab handleNext={handleNext} logo={logo} basketId={basketId} />
      ),
      value: "KYC",
      id: "kyc",
    },
    {
      name: "Risk Profile",
      icon: BulletedListIcon,
      isCompleted: form.getValues("clientObject.riskprofileDetails.points"),
      disabled: form.getValues("clientObject.riskprofileDetails.points"),
      show: form.getValues(
        "clientObject.onboardDetails.customizeSetting.riskprofiling",
      ),
      component: <AssessmentTab basketId={basketId} handleNext={handleNext} />,
      value: "RISK_PROFILE",
      id: "risk_profile",
    },
    {
      name: "Agreement",
      icon: DocumentIcon,
      isCompleted: form.getValues(
        "clientObject.agreementDetails.agreementStatus.id",
      ),
      disabled: form.getValues(
        "clientObject.agreementDetails.agreementStatus.id",
      ),
      show: form.getValues(
        "clientObject.onboardDetails.customizeSetting.agreement",
      ),
      component: <Agreementtab handleNext={handleNext} basketId={basketId} />,
      value: "AGREEMENT",
      id: "agreement",
    },
    {
      name: "Payment",
      icon: CreditCardIcon,
      show: form.getValues(
        "clientObject.onboardDetails.customizeSetting.payment",
      ),
      component: <PaymentTab />,
      value: "PAYMENT",
      id: "payment",
    },
    {
      name: "Completed",
      icon: CheckIcon,
      show:
        form.getValues("clientObject.id") &&
        !form.getValues("clientObject.onboardDetails.customizeSetting.payment"),
      component: <CompletedStep />,
      value: "COMPLETED",
      id: "completed",
    },
  ];

  const filteredTabs = tabs.filter((tab) => tab?.show);
  const activeTabValue = filteredTabs[activeStepWatch]?.value;

  const onSubmit = (values: any) => {
    const currentTabValue = filteredTabs[activeStepWatch]?.value;

    if (currentTabValue === "DETAILS") {
      handleClientCreation(values.clientCreation);
    } else if (
      currentTabValue === "BASKET" ||
      currentTabValue === "INVESTMENT" ||
      currentTabValue === "AGREEMENT" ||
      currentTabValue === "KYC"
    ) {
      handleNext();
    } else if (currentTabValue === "UPLOAD_PROOF") {
      const formData = new FormData();

      formData.append("freshCapital", values.planDataObject.aumCash);
      formData.append(
        "transactions",
        isEmpty(values?.planDataObject?.plansHoldings[0].gridCode)
          ? JSON.stringify([])
          : JSON.stringify(
              values?.planDataObject?.plansHoldings?.map(
                (d: {
                  gridCode: string;
                  quantity: string | number;
                  cmp: string | number;
                }) => {
                  return {
                    gridCode: d.gridCode,
                    quantity: d.quantity,
                    cmp: d.cmp,
                  };
                },
              ),
            ),
      );
      formData.append("portfolio", form.getValues("clientObject.portfolio.id"));
      formData.append(
        "frequency",
        form.getValues("clientObject.advanceFeePlan.frequency"),
      );
      formData.append(
        "amount",
        JSON.stringify(
          handleCalculateFee({
            feePercent: form.getValues(
              "clientObject.advanceFeePlan.feePlan.managementFee.fee",
            ),
            total:
              +values.planDataObject.aumCash +
              sum(
                values.planDataObject.plansHoldings.map(
                  (d: { cmp: string; quantity: string }) =>
                    +d.cmp * +d.quantity,
                ),
              ),
          }),
        ),
      );
      formData.append(
        "holdingsValue",
        JSON.stringify(
          sum(
            values?.planDataObject?.plansHoldings?.map(
              (d: { cmp: string; quantity: string }) => +d.cmp * +d.quantity,
            ),
          ),
        ),
      );
      formData.append("startDate", dayjs(new Date()).format("YYYY-MM-DD"));
      values.planDataObject?.holdingsProofs?.forEach(
        (file: string | Blob, index: any) => {
          formData.append(`holdingsProof[${index}]`, file);
        },
      );
      values.planDataObject?.cashProofs?.forEach(
        (file: string | Blob, index: any) => {
          formData.append(`capitalProof[${index}]`, file);
        },
      );

      uploadProof(formData);
    } else if (currentTabValue === "RISK_PROFILE") {
      handleNext();
    }
  };

  const clientCreationLoading = !!useIsMutating({
    mutationKey: ["CLIENT_CREATION"],
  });

  const uploadProofLoading = !!useIsMutating({
    mutationKey: ["UPLOAD_PROOF_FOR_ONBOARDING"],
  });

  return (
    <FormProvider {...form}>
      <form
        style={{ height: "100%", width: "100%" }}
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <Grid2 container bgcolor={"var(--white)"}>
          <Grid2 size={12} borderBottom={`1px solid var(--neutral-100)`} p={2}>
            <img
              src={logo || "/Logos/iconFullLogo.png"}
              alt=""
              height={`-webkit-fill-available`}
              width={"auto"}
              style={{ maxHeight: "32px" }}
              onError={({ currentTarget }) => {
                currentTarget.src = "https://gridkey.in/Logos/iconFullLogo.png";
              }}
            />
          </Grid2>
          <Grid2 size={12}>
            <Tabs
              variant="scrollable"
              value={activeStepWatch}
              scrollButtons={false}
              TabIndicatorProps={{
                style: { backgroundColor: "var(--primary-900)" },
              }}
              onChange={(e, val) =>
                getLocalEnvironment && form.setValue("activeStep", val)
              }
            >
              {filteredTabs?.map((dataItem, index) => (
                <Tab
                  sx={{
                    p: "24px !important",
                    gap: 1,
                    color: handleReturnStepColor({
                      dataItem: dataItem,
                      index: index,
                    }),
                    "&.Mui-disabled": {
                      color: handleReturnStepColor({
                        dataItem: dataItem,
                        index: index,
                      }),
                    },
                  }}
                  disabled={dataItem?.disabled}
                  key={dataItem.id}
                  label={dataItem.name}
                  icon={
                    dataItem?.isCompleted ? (
                      <CheckListIcon color={"var(--success-700)"} />
                    ) : (
                      <dataItem.icon />
                    )
                  }
                  iconPosition="start"
                />
              ))}
            </Tabs>
            <Divider />
          </Grid2>
          <Grid2
            size={12}
            maxHeight={`calc(100vh - 200px)`}
            minHeight={`calc(100vh - 200px)`}
            overflow={"hidden auto"}
            px={2}
          >
            {isFetching || clientCreationLoading || existClientLoading ? (
              <Grid2
                container
                size={12}
                display={"flex"}
                justifyContent={"center"}
                alignItems={"center"}
                height={"100%"}
              >
                <CircularProgress />
              </Grid2>
            ) : (
              <Grid2 container size={12} spacing={1}>
                {filteredTabs?.map((tab, index) => (
                  <Show.When key={index} isTrue={activeStepWatch === index}>
                    <Grid2 size={12}>
                      <AnimationComponent>{tab.component}</AnimationComponent>
                    </Grid2>
                  </Show.When>
                ))}
              </Grid2>
            )}
          </Grid2>
          <Grid2
            size={12}
            display={"flex"}
            justifyContent={
              activeStepWatch === 0 ? "flex-end" : "space-between"
            }
            gap={1}
            alignItems={"center"}
            p={2}
          >
            {activeStepWatch !== 0 && (
              <GKButton
                variant="outlined"
                color="dark"
                onClick={() => {
                  handlePrevious();
                }}
                startIcon={<ArrowLeftIcon />}
              >
                Previous
              </GKButton>
            )}

            {activeTabValue === "AGREEMENT" && (
              <GKButton
                variant="outlined"
                color="dark"
                sx={{ ml: "auto" }}
                target="_blank"
                href={form.getValues("clientObject.agreementTemplate")}
              >
                Read Agreement &nbsp;
              </GKButton>
            )}

            {activeTabValue !== "PAYMENT" && activeTabValue !== "COMPLETED" && (
              <GKButton
                variant="contained"
                type="submit"
                loading={
                  clientCreationLoading || isFetching || uploadProofLoading
                }
                endIcon={<ArrowRightIcon />}
                disabled={
                  (activeTabValue === "KYC" &&
                    !form.getValues("clientObject.kycDetails.kycId")) ||
                  (activeTabValue === "AGREEMENT" &&
                    !form.getValues(
                      "clientObject.agreementDetails.agreementStatus.id",
                    ))
                }
              >
                {activeTabValue === "UPLOAD_PROOF" &&
                form.watch("planDataObject.holdingsProofs").length === 0 &&
                form.watch("planDataObject.cashProofs").length === 0
                  ? "Skip & Next"
                  : "Next"}
                &nbsp;
              </GKButton>
            )}
          </Grid2>
        </Grid2>

        <Show.When isTrue={holdingModalWatch}>
          <PlansHoldingsModal
            open={!!holdingModalWatch}
            setOpen={() =>
              form.setValue("planDataObject.showHoldingsModal", false)
            }
          />
        </Show.When>
      </form>
    </FormProvider>
  );
};

export default OnBoarding;
